// 라우트/언어별 문서 메타(title · description · canonical · OG · 트위터 카드 · JSON-LD)를
// 런타임에 갱신합니다.
//
// public/index.html 의 정적 태그는 JS를 실행하지 않는 크롤러(카카오톡·페이스북 링크 미리보기)가
// 읽는 "초기값"이고, 여기서는 SPA 라우팅·언어 전환에 맞춰 그 값을 덮어씁니다.
// 정적 태그만으로는 라우트별 구분이 불가능하므로 둘 다 필요합니다.

import i18n from '../i18n';

export const SITE_URL = 'https://lansly.toyple.net';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

// og:locale 은 언어 코드가 아니라 locale 표기를 씁니다.
const OG_LOCALE = {
  ko: 'ko_KR',
  en: 'en_US',
  ja: 'ja_JP',
  zh: 'zh_CN',
};

const JSON_LD_ID = 'lansly-route-jsonld';

/** <head> 안의 meta 태그를 찾아 갱신하고, 없으면 만들어 넣습니다. */
const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

/** 라우트에 붙은 JSON-LD 는 라우트가 바뀔 때마다 통째로 교체합니다. */
const setJsonLd = (data) => {
  const prev = document.getElementById(JSON_LD_ID);
  if (prev) prev.remove();
  if (!data) return;
  const el = document.createElement('script');
  el.type = 'application/ld+json';
  el.id = JSON_LD_ID;
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
};

// 언어가 바뀌었을 때 같은 라우트의 메타를 다시 그리기 위해 마지막 값을 보관합니다.
let current = null;

/**
 * @param {object}  descriptor
 * @param {string}  descriptor.title        완성된 문서 제목
 * @param {string}  descriptor.description  meta description
 * @param {string}  descriptor.path         canonical 경로 (예: '/community')
 * @param {boolean} [descriptor.noindex]    로그인 이후 화면 등 색인 제외 대상
 * @param {string}  [descriptor.image]      OG 이미지 절대 URL
 * @param {object}  [descriptor.jsonLd]     라우트 전용 구조화 데이터
 */
export const applySeo = (descriptor) => {
  if (!descriptor) return;
  current = descriptor;

  const { title, description, path = '/', noindex, image, jsonLd } = descriptor;
  const url = `${SITE_URL}${path === '/' ? '/' : path}`;
  const ogImage = image || DEFAULT_OG_IMAGE;
  const lang = i18n.resolvedLanguage || 'ko';

  if (title) document.title = title;
  setMeta('name', 'description', description);
  setLink('canonical', url);

  setMeta(
    'name',
    'robots',
    noindex
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  );

  setMeta('property', 'og:type', 'website');
  setMeta('property', 'og:site_name', 'Lansly');
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:image', ogImage);
  setMeta('property', 'og:locale', OG_LOCALE[lang] || 'ko_KR');

  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image', ogImage);

  setJsonLd(jsonLd);
};

// 언어 전환 시 현재 라우트 기준으로 다시 적용.
// i18n/index.js 가 먼저 사이트 기본값으로 되돌려 놓으므로, 그 뒤에 라우트 값으로 덮어씁니다.
// (이 모듈이 i18n 을 import 하므로 리스너 등록은 항상 i18n 쪽보다 나중입니다.)
i18n.on('languageChanged', () => {
  if (current) applySeo(current);
});
